import React, { useState } from 'react';
import { Loader, AlertTriangle, Copy, Users, FileText } from 'lucide-react';
import { abaNoteGenerationService, AbaTemplate } from '../services/AbaNoteGenerationService';
import { sessionSummariesService } from '../services/SessionSummariesService';
import { sessionNoteService } from '../services/SessionNoteService';
import { SessionNote } from '../models/SessionNote';

interface Props {
  selectedNote: SessionNote;
  onUpdate: (updated: SessionNote) => void;
  onUseAsClinicalNotes?: (content: string) => void;
}

const TEMPLATE_OPTIONS: { value: AbaTemplate; label: string; help: string }[] = [
  { value: 'SOAP', label: 'SOAP', help: 'Generic clinical Subjective / Objective / Assessment / Plan' },
  { value: 'ABC', label: 'ABC', help: 'Antecedent → Behavior → Consequence functional analysis' },
  { value: 'DTT', label: 'DTT', help: 'Discrete Trial Training: programs, trials, prompts, mastery' },
  { value: 'NET', label: 'NET', help: 'Natural Environment Training narrative' },
  { value: 'Progress', label: 'Progress', help: 'Goal-by-goal status for treatment plan reviews' },
];

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-6)',
  marginBottom: 'var(--spacing-4)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: 'bold',
  color: '#6d28d9',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const buttonStyle = (active: boolean): React.CSSProperties => ({
  backgroundColor: active ? '#6d28d9' : 'white',
  color: active ? 'white' : '#6d28d9',
  border: '2px solid #6d28d9',
  padding: '8px 14px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
});

const generateButtonStyle: React.CSSProperties = {
  backgroundColor: '#6d28d9',
  color: 'white',
  padding: '12px 20px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '15px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const confidencePill = (confidence: number): React.CSSProperties => {
  const tone =
    confidence >= 0.8
      ? { bg: '#d1fae5', fg: '#065f46' }
      : confidence >= 0.5
      ? { bg: '#fef3c7', fg: '#92400e' }
      : { bg: '#fee2e2', fg: '#991b1b' };
  return {
    backgroundColor: tone.bg,
    color: tone.fg,
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
};

export const AiAssistedNoteSection: React.FC<Props> = ({
  selectedNote,
  onUpdate,
  onUseAsClinicalNotes,
}) => {
  const [template, setTemplate] = useState<AbaTemplate>(
    (selectedNote.aiNoteDraft?.template as AbaTemplate) || 'SOAP'
  );

  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [generatingSummaries, setGeneratingSummaries] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = selectedNote.aiNoteDraft;
  const summaries = selectedNote.sessionSummaries;
  const hasRbtDraft = !!selectedNote.rbtDraft;

  const refreshNote = async () => {
    const updated = await sessionNoteService.getSessionNote(selectedNote.id);
    if (updated) onUpdate(updated);
  };

  const handleGenerateDraft = async () => {
    setError(null);
    setGeneratingDraft(true);
    try {
      await abaNoteGenerationService.generateNote(selectedNote.id, template);
      await refreshNote();
    } catch (err: any) {
      setError(err.message || 'Failed to generate ABA draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const handleGenerateSummaries = async () => {
    setError(null);
    setGeneratingSummaries(true);
    try {
      await sessionSummariesService.generateSummaries(selectedNote.id);
      await refreshNote();
    } catch (err: any) {
      setError(err.message || 'Failed to generate summaries');
    } finally {
      setGeneratingSummaries(false);
    }
  };

  if (!hasRbtDraft) {
    return null; // nothing to draft from
  }

  return (
    <>
      {/* Card 1: ABA Template Draft */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <h3 style={sectionTitleStyle}>
            <FileText size={22} />
            AI ABA Note Draft
          </h3>
          <button
            onClick={handleGenerateDraft}
            disabled={generatingDraft}
            style={{ ...generateButtonStyle, opacity: generatingDraft ? 0.6 : 1 }}
          >
            {generatingDraft ? (
              <>
                <Loader className="animate-spin" size={18} />
                Generating...
              </>
            ) : (
              <>
                <FileText size={18} />
                {draft ? `Regenerate ${template} Draft` : `Generate ${template} Draft`}
              </>
            )}
          </button>
        </div>

        {/* Template picker */}
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              fontSize: '13px',
              color: '#6b6b6b',
              marginBottom: '8px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Template
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {TEMPLATE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTemplate(opt.value)}
                title={opt.help}
                style={buttonStyle(template === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
            {TEMPLATE_OPTIONS.find((o) => o.value === template)?.help}
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {generatingDraft && !draft && (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 20px',
              backgroundColor: '#f5f3ff',
              borderRadius: '8px',
              border: '2px dashed #8b5cf6',
            }}
          >
            <Loader className="animate-spin" size={32} style={{ margin: '0 auto', color: '#8b5cf6' }} />
            <p style={{ marginTop: '12px', color: '#6b21a8', fontSize: '15px' }}>
              Claude is drafting an ABA-formatted note... ~10–20s
            </p>
          </div>
        )}

        {draft && (
          <div>
            {/* Confidence + warnings header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
                flexWrap: 'wrap',
              }}
            >
              <span style={confidencePill(draft.confidence)}>
                Confidence: {(draft.confidence * 100).toFixed(0)}%
              </span>
              <span style={{ fontSize: '12px', color: '#6b6b6b' }}>
                Template: <strong>{draft.template}</strong> · Model:{' '}
                <code style={{ fontSize: '11px' }}>{draft.modelUsed}</code>
              </span>
            </div>

            {draft.warnings && draft.warnings.length > 0 && (
              <div
                style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  marginBottom: '14px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#92400e',
                    fontWeight: 600,
                    marginBottom: '6px',
                    fontSize: '14px',
                  }}
                >
                  <AlertTriangle size={16} />
                  Review needed ({draft.warnings.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#78350f' }}>
                  {draft.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#1f2937',
                backgroundColor: '#fafafa',
                border: '1px solid #e5e0db',
                borderRadius: '6px',
                padding: '14px',
                margin: 0,
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              {draft.content}
            </pre>

            {onUseAsClinicalNotes && (
              <button
                onClick={() => onUseAsClinicalNotes(draft.content)}
                style={{
                  marginTop: '12px',
                  backgroundColor: '#6d28d9',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Copy size={14} />
                Use as Clinical Notes
              </button>
            )}

            {draft.sources && draft.sources.length > 0 && (
              <details style={{ marginTop: '12px' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#6b6b6b',
                    fontWeight: 600,
                  }}
                >
                  Show sources ({draft.sources.length})
                </summary>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '13px', color: '#4b5563' }}>
                  {draft.sources.map((s, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>
                      {s}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Card 2: Dual Summaries */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <h3 style={{ ...sectionTitleStyle, color: '#4F6367' }}>
            <Users size={22} />
            AI Session Summaries
          </h3>
          <button
            onClick={handleGenerateSummaries}
            disabled={generatingSummaries}
            style={{
              ...generateButtonStyle,
              backgroundColor: '#4F6367',
              opacity: generatingSummaries ? 0.6 : 1,
            }}
          >
            {generatingSummaries ? (
              <>
                <Loader className="animate-spin" size={18} />
                Generating...
              </>
            ) : (
              <>
                <FileText size={18} />
                {summaries ? 'Regenerate Summaries' : 'Generate Summaries'}
              </>
            )}
          </button>
        </div>

        {generatingSummaries && !summaries && (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 20px',
              backgroundColor: '#f0fdfa',
              borderRadius: '8px',
              border: '2px dashed #14b8a6',
            }}
          >
            <Loader className="animate-spin" size={32} style={{ margin: '0 auto', color: '#4F6367' }} />
            <p style={{ marginTop: '12px', color: '#4F6367', fontSize: '15px' }}>
              Generating supervisor + parent summaries...
            </p>
          </div>
        )}

        {summaries && (
          <div>
            {/* Risk flags banner */}
            {summaries.riskFlags && summaries.riskFlags.length > 0 && (
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  padding: '14px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#991b1b',
                    fontWeight: 700,
                    marginBottom: '8px',
                  }}
                >
                  <AlertTriangle size={18} />
                  Risk Flags ({summaries.riskFlags.length}) — supervisor must review before sharing
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#7f1d1d' }}>
                  {summaries.riskFlags.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Confidence + warnings */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '14px',
                flexWrap: 'wrap',
              }}
            >
              <span style={confidencePill(summaries.confidence)}>
                Confidence: {(summaries.confidence * 100).toFixed(0)}%
              </span>
              <span style={{ fontSize: '12px', color: '#6b6b6b' }}>
                Model: <code style={{ fontSize: '11px' }}>{summaries.modelUsed}</code>
              </span>
            </div>

            {summaries.warnings && summaries.warnings.length > 0 && (
              <div
                style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  marginBottom: '14px',
                  fontSize: '13px',
                  color: '#78350f',
                }}
              >
                <strong>Warnings:</strong> {summaries.warnings.join(' · ')}
              </div>
            )}

            {/* Two-column summaries */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '14px',
                marginBottom: '14px',
              }}
            >
              <div
                style={{
                  backgroundColor: '#f0fdfa',
                  border: '1px solid #99f6e4',
                  borderRadius: '8px',
                  padding: '14px',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: '#4F6367',
                    fontSize: '14px',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Supervisor Summary (clinical)
                </div>
                <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#134e4a', whiteSpace: 'pre-wrap' }}>
                  {summaries.supervisorSummary}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#fff7ed',
                  border: '1px solid #fdba74',
                  borderRadius: '8px',
                  padding: '14px',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: '#c2410c',
                    fontSize: '14px',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Users size={14} />
                  Parent Summary (family-facing)
                </div>
                <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#7c2d12', whiteSpace: 'pre-wrap' }}>
                  {summaries.parentSummary}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#9a3412',
                    marginTop: '10px',
                    fontStyle: 'italic',
                  }}
                >
                  Review and edit before sharing with the family.
                </div>
              </div>
            </div>

            {/* Structured chips */}
            {(summaries.keyTopics?.length ||
              summaries.interventionsUsed?.length ||
              summaries.homeworkAssigned?.length ||
              summaries.nextSessionFocus?.length) && (
              <div
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  fontSize: '13px',
                  color: '#374151',
                }}
              >
                {summaries.keyTopics?.length > 0 && (
                  <ChipRow label="Key topics" items={summaries.keyTopics} />
                )}
                {summaries.interventionsUsed?.length > 0 && (
                  <ChipRow label="Interventions" items={summaries.interventionsUsed} />
                )}
                {summaries.homeworkAssigned?.length > 0 && (
                  <ChipRow label="Homework" items={summaries.homeworkAssigned} />
                )}
                {summaries.nextSessionFocus?.length > 0 && (
                  <ChipRow label="Next session focus" items={summaries.nextSessionFocus} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const ChipRow: React.FC<{ label: string; items: string[] }> = ({ label, items }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '6px' }}>
    <span style={{ fontWeight: 700, minWidth: '120px', color: '#4b5563' }}>{label}:</span>
    <span style={{ flex: 1 }}>{items.join(' · ')}</span>
  </div>
);
